
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  useEffect(() => {
    // Update page title and meta description
    document.title = 'Contact Us - XJ Global';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact XJ Global for beauty products, battery solutions, or general business inquiries. Connect with XJ Beauty and XJ Battery divisions.');
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact XJ Global",
      "description": "Contact XJ Global for beauty products, battery solutions, or general business inquiries",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contact`,
      "mainEntity": {
        "@type": "Organization",
        "name": "XJ Global",
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "contactType": "Customer Service",
            "email": "info@xj-global.com",
            "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contact`
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      // Reset title
      document.title = 'XJ Global - Leading Global Supply Chain Solutions';
      if (metaDescription) {
        metaDescription.setAttribute('content', 'XJ Global is a leading sourcing, product development and supply chain management company for the world\'s largest retail chain stores and brands.');
      }
    };
  }, []);

  const contacts = [
    {
      title: 'XJ BEAUTY',
      description: 'Beauty and personal care products',
      icon: 'ri-palette-line',
      link: 'https://www.xj-beauty.com',
      isWebsite: true,
    },
    {
      title: 'XJ BATTERY',
      description: 'Alkaline battery solutions',
      icon: 'ri-battery-line',
      link: 'https://www.xj-battery.com',
      isWebsite: true,
    },
    {
      title: 'General Inquiry',
      description: 'For all other business inquiries',
      icon: 'ri-mail-line',
      link: 'mailto:info@xj-global.com',
      isWebsite: false,
    },
  ];

  const handleContactClick = (contact: any) => {
    if (contact.isWebsite) {
      window.open(contact.link, '_blank');
    } else {
      // Scroll to bottom of page to show email info
      window.scrollTo({ 
        top: document.body.scrollHeight, 
        behavior: 'smooth' 
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto">
            Connect with our specialized divisions or reach out for general inquiries
          </p>
        </div>
      </div>

      {/* Contact Options */}
      <div className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {contacts.map((contact, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow border border-gray-100">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${contact.icon} text-3xl text-teal-600`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {contact.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {contact.description}
                </p>
                <button
                  onClick={() => handleContactClick(contact)}
                  className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {contact.isWebsite ? 'Visit Website' : 'Send Email'}
                  <div className="ml-2 w-4 h-4 flex items-center justify-center">
                    <i className={contact.isWebsite ? 'ri-external-link-line' : 'ri-mail-line'}></i>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Contact Info */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Get In Touch
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Whether you're looking for beauty products, battery solutions, or have general business inquiries, we're here to help you find the right solutions.
          </p>
          <div className="bg-white rounded-lg p-8 shadow-sm">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                <i className="ri-phone-line text-2xl text-teal-600"></i>
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Need Immediate Assistance?
            </h3>
            <p className="text-gray-600">
              Contact our team directly at{' '}
              <a 
                href="mailto:info@xj-global.com" 
                className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer"
              >
                info@xj-global.com
              </a>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
